import { useRecipeStockSettings } from "@/features/recipes/hooks/useRecipeStockSettings";
import { RecipeStockSettingsFields } from "@/features/recipes/components/RecipeStockSettingsFields";
import { TaxExample } from "@/features/settings/components/SettingsExamples";
import {
	ProductSetupEditor,
	emptyConfiguration,
	componentCost,
	ConfigurationDraft,
} from "../components/ProductCompositionEditor";
import { ProductLabel } from "../components/ProductLabel";
import { ProductPreview } from "../components/ProductPreview";
import { ProductScalePanel } from "../components/ProductScalePanel";
import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Save, Plus, Package, Wand2, Truck, Camera } from "lucide-react";
import {
	productsAPI,
	categoriesAPI,
	branchesAPI,
	modifiersAPI,
	recipesAPI,
	stockTransfersAPI,
	taxesAPI,
	getApiErrorPayload,
} from "@/lib/api";
import { getDashboardProductImageSrc } from "@/lib/productImageUrl";
import { barcodeFieldError } from "@/lib/ean13";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { CardDescription } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { usePermission } from "@/hooks/usePermission";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { Loading } from "@/components/ui/loader";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { useIsDirty, useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";

interface Product {
	id: number;
	name: string;
	barcode: string;
	plu?: string;
	description?: string;
	categoryId?: number;
	category?: {
		id: number;
		name: string;
	};
	branchId?: number;
	branch?: {
		id: number;
		name: string;
	};
	trackQuantity: boolean;
	unit: "piece" | "bulk" | "weight";
	type: "product" | "service" | "menu_item" | "combo";
	quantity: number;
	sellingPrice: number;
	purchasePrice: number;
	/** Reference to a company tax (GET /taxes); the server pins taxRate from it. Null on legacy manual-rate products. */
	taxId?: number | null;
	taxRate: number;
	discountRate: number;
	discountValue: number;
	image?: string;
	isActive: boolean;
	status?: "active" | "inactive" | "out_of_stock" | "discontinued";
	/** Threshold quantity; a low-stock notification fires when stock reaches it (0 = off). */
	lowStockAlert: number;
	warrantyEnabled: boolean;
	warrantyDuration: string;
	warrantyUnit: "days" | "months" | "years";
	warrantyTerms: string;
	warrantyProvider: string;
	requiresSerialNumber: boolean;
	createdAt: string;
	updatedAt: string;
}

interface Category {
	id: number;
	name: string;
	status: string;
}

interface Branch {
	id: number;
	name: string;
	status: string;
}

interface Tax {
	id: number;
	name: string;
	rate: number;
	isActive: boolean;
}

interface ProductPayload {
	name: string;
	description?: string;
	type: "product" | "service" | "menu_item" | "combo";
	unit: "piece" | "bulk" | "weight";
	sellingPrice: number;
	purchasePrice: number;
	taxId?: number | null;
	taxRate?: number;
	discountRate?: number;
	discountValue?: number;
	quantity: number;
	barcode?: string;
	sku?: string;
	plu?: string;
	image?: string;
	lowStockAlert?: number;
	trackQuantity?: boolean;
	isWeightBased?: boolean;
	isKit?: boolean;
	weight?: number;
	warrantyEnabled?: boolean;
	warrantyDuration?: number;
	warrantyUnit?: "days" | "months" | "years";
	warrantyTerms?: string;
	warrantyProvider?: string;
	requiresSerialNumber?: boolean;
	categoryId?: number;
	branchId?: number;
}

const ProductDetailPage: React.FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const currency = useCurrency();
	useLanguage();
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [barcodeError, setBarcodeError] = useState("");
	const [generatingBarcode, setGeneratingBarcode] = useState(false);
	const [categories, setCategories] = useState<Category[]>([]);
	const [branches, setBranches] = useState<Branch[]>([]);
	const [taxes, setTaxes] = useState<Tax[]>([]);
	const [taxesLoading, setTaxesLoading] = useState(true);
	const [taxesError, setTaxesError] = useState(false);
	const canReadTaxes = usePermission("taxes.read");
	const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);
	const [newCategoryName, setNewCategoryName] = useState("");
	const [configuration, setConfiguration] = useState<ConfigurationDraft>(emptyConfiguration);
	const [configurationBaseline, setConfigurationBaseline] = useState("");
	const [configurationLoading, setConfigurationLoading] = useState(false);
	const [configurationError, setConfigurationError] = useState(false);
	const canReadModifiers = usePermission("products.read");
	const canWriteModifiers = usePermission(id ? "products.update" : "products.create");
	const canReadRecipe = usePermission("recipes.read");
	const canWriteRecipe = usePermission(id ? "recipes.update" : "recipes.create");
	const [transferOpen, setTransferOpen] = useState(false);
	const [transferToBranchId, setTransferToBranchId] = useState<number | "">("");
	const [transferQty, setTransferQty] = useState(1);
	const [transferNotes, setTransferNotes] = useState("");
	const [transferSubmitting, setTransferSubmitting] = useState(false);
	const [product, setProduct] = useState<Product>({
		id: 0,
		name: "",
		barcode: "",
		plu: "",
		description: "",
		categoryId: undefined,
		branchId: undefined,
		trackQuantity: true,
		unit: "piece",
		type: "product",
		quantity: 0,
		sellingPrice: 0,
		purchasePrice: 0,
		taxId: null,
		taxRate: 0,
		discountRate: 0,
		discountValue: 0,
		image: "",
		isActive: true,
		lowStockAlert: 0,
		warrantyEnabled: false,
		warrantyDuration: "",
		warrantyUnit: "months",
		warrantyTerms: "",
		warrantyProvider: "",
		requiresSerialNumber: false,
		createdAt: "",
		updatedAt: "",
	});

	const isEditing = !!id;
	const canEdit = usePermission(["inventory.update", "products.update"]);
	const canCreate = usePermission(["inventory.create", "products.create"]);
	const canSave = isEditing ? canEdit : canCreate;
	const isViewOnly = isEditing && !canEdit;

	// On a new product, the generated barcode does not count as an edit.
	const { barcode: _barcode, ...productWithoutGenerated } = product;
	const { isDirty, markSaved } = useIsDirty(
		isEditing ? product : productWithoutGenerated,
		isEditing ? !loading && product.id > 0 : true,
	);

	useEffect(() => {
		fetchCategories();
		fetchBranches();
		fetchTaxes();
		if (isEditing) {
			fetchProduct();
		} else {
			generateBarcode();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on id change only
	}, [id]);

	const composed = product.type === "menu_item" || product.type === "combo";
	const canReadStockPolicy = usePermission(["settings.read", "recipes.read"]);
	const canWriteStockPolicy = usePermission(["settings.update", "recipes.update"]);
	const stockPolicy = useRecipeStockSettings(product.type === "menu_item" && canReadStockPolicy);
	const configurationDirty = JSON.stringify(configuration) !== configurationBaseline;
	const loadConfiguration = async (selectedProduct: Product = product) => {
		if (!selectedProduct.id || !composed) return;
		setConfigurationLoading(true);
		setConfigurationError(false);
		try {
			const [groups, recipe, components] = await Promise.all([
				canReadModifiers ? modifiersAPI.getByProductId(selectedProduct.id) : [],
				selectedProduct.type === "menu_item" && canReadRecipe
					? recipesAPI.getByProductId(selectedProduct.id)
					: null,
				selectedProduct.type === "combo" ? productsAPI.getKitItems(selectedProduct.id) : [],
			]);
			const savedVariants =
				(selectedProduct as Product & { variants?: ConfigurationDraft["variants"] }).variants || [];
			const productIds = Array.from(new Set(savedVariants.flatMap(v => v.components.map(c => c.productId))));
			const products = await Promise.all(productIds.map(id => productsAPI.getById(id)));
			const next = {
				groups: (groups as ConfigurationDraft["groups"]).map(g => ({
					...g,
					options: g.options.map(o => ({
						...o,
						priceAdjustment: Number(o.priceAdjustment),
						extraQuantity: o.extraQuantity == null ? null : Number(o.extraQuantity),
					})),
				})),
				variants: savedVariants.map(v => ({
					...v,
					components: v.components.map(line => ({
						...line,
						product: products.find(p => p.id === line.productId),
						wastePercent: line.wastePercent || 0,
						unit: line.unit || "piece",
					})),
				})),
				ingredients: (recipe?.ingredients || []).map((line: any) => ({
					...line,
					quantity: Number(line.quantity),
					wastePercent: Number(line.wastePercent),
				})),
				components: (components || []).map((line: any) => ({
					productId: line.childProductId,
					variantId: line.childVariantId || undefined,
					product: line.childProduct,
					quantity: Number(line.quantity),
					unit: line.childProduct?.unit === "weight" ? "kilogram" : "piece",
					wastePercent: 0,
				})),
			};
			setConfiguration(next);
			setConfigurationBaseline(JSON.stringify(next));
		} catch {
			setConfigurationError(true);
		} finally {
			setConfigurationLoading(false);
		}
	};
	useEffect(() => {
		if (product.id && composed) void loadConfiguration();
		else if (!product.id) setConfigurationBaseline(JSON.stringify(emptyConfiguration()));
		// Only reload when opening a saved item; changing the draft type must preserve drafts.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product.id]);
	const calculatedCost =
		composed && configuration.variants.length
			? componentCost(
					configuration.variants.reduce((a, b) => (a.sellingPrice <= b.sellingPrice ? a : b)).components,
					product.type === "menu_item",
				)
			: composed
				? componentCost(
						product.type === "menu_item" ? configuration.ingredients : configuration.components,
						product.type === "menu_item",
					)
				: Number(product.purchasePrice);

	const displaySellingPrice =
		composed && configuration.variants.length
			? Math.min(...configuration.variants.map(v => v.sellingPrice))
			: Number(product.sellingPrice);

	const { allowNavigation } = useUnsavedChangesGuard(
		(isDirty || (composed && configurationDirty) || stockPolicy.dirty) && !saving && !isViewOnly,
	);
	const fetchCategories = async () => {
		try {
			const data = await categoriesAPI.getAll();
			setCategories(data);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("products.errors.fetchCategoriesFailed"),
				variant: "destructive",
			});
		}
	};

	const fetchBranches = async () => {
		try {
			const data = await branchesAPI.getAll();
			setBranches(data);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("products.errors.fetchBranchesFailed"),
				variant: "destructive",
			});
		}
	};

	const fetchTaxes = async () => {
		setTaxesLoading(true);
		setTaxesError(false);
		try {
			const data = await taxesAPI.getAll();
			setTaxes(Array.isArray(data) ? data.filter((tax: Tax) => tax.isActive) : []);
		} catch (error) {
			// Retain the current product assignment when the options cannot be read.
			setTaxesError(true);
		} finally {
			setTaxesLoading(false);
		}
	};

	const fetchProduct = async () => {
		try {
			setLoading(true);
			const data = await productsAPI.getById(id!);
			setProduct(data);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("products.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/products");
		} finally {
			setLoading(false);
		}
	};

	const generateBarcode = async () => {
		setGeneratingBarcode(true);
		try {
			const { barcode } = await productsAPI.generateBarcode(product.branchId ?? undefined);
			setBarcodeError("");
			setProduct(prev => ({ ...prev, barcode }));
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		} finally {
			setGeneratingBarcode(false);
		}
	};

	const onBarcodeChange = (value: string) => {
		handleInputChange("barcode", value);
		setBarcodeError(barcodeFieldError(value));
	};

	const handleInputChange = (field: keyof Product, value: any) => {
		setProduct(prev => ({ ...prev, [field]: value }));
	};

	// Tax select sentinels (Radix Select forbids empty item values).
	const TAX_EXEMPT = "exempt";
	const TAX_LEGACY = "legacy";

	/** Current tax choice: a tax id, "legacy" (manual rate on older products), or "exempt". */
	const taxSelectValue =
		product.taxId != null ? String(product.taxId) : Number(product.taxRate) > 0 ? TAX_LEGACY : TAX_EXEMPT;

	const handleTaxSelect = (selected: string) => {
		if (selected === TAX_EXEMPT) {
			setProduct(prev => ({ ...prev, taxId: null, taxRate: 0 }));
			return;
		}
		if (selected === TAX_LEGACY) {
			setProduct(prev => ({ ...prev, taxId: null }));
			return;
		}
		const tax = taxes.find(item => String(item.id) === selected);
		setProduct(prev => ({
			...prev,
			taxId: Number(selected),
			// Optimistic preview; the server re-pins the rate from taxId on save.
			taxRate: tax ? Number(tax.rate) || 0 : prev.taxRate,
		}));
	};

	const buildProductPayload = (value: Product): ProductPayload => {
		const source = value as unknown as Record<string, unknown>;

		return {
			name: value.name,
			description: value.description || undefined,
			type: value.type,
			unit: value.unit,
			sellingPrice: Number(value.sellingPrice) || 0,
			purchasePrice:
				composed && (!isEditing || configurationDirty)
					? Number(calculatedCost.toFixed(2))
					: Number(value.purchasePrice) || 0,
			// The server pins taxRate from taxId when set; the sent taxRate only
			// matters for legacy manual-rate products (taxId null).
			taxId: value.taxId ?? null,
			taxRate: Number(value.taxRate) || 0,
			discountRate: Number(value.discountRate) || 0,
			discountValue: Number(value.discountValue) || 0,
			quantity: Number(value.quantity) || 0,
			// Weight labels identify the item by PLU; omit the standard barcode to preserve any stored legacy value.
			barcode: value.unit === "weight" ? undefined : value.barcode || undefined,
			sku: String(source.sku ?? "").trim() || undefined,
			plu: value.unit === "weight" ? value.plu?.trim() || undefined : undefined,
			image: value.image || undefined,
			lowStockAlert: Number(value.lowStockAlert) || 0,
			trackQuantity: value.type === "product" && Boolean(value.trackQuantity),
			isWeightBased: value.unit === "weight",
			isKit: value.type === "combo",
			weight: typeof source.weight === "number" ? source.weight : undefined,
			warrantyEnabled: Boolean(value.warrantyEnabled),
			warrantyDuration: value.warrantyEnabled ? Number(value.warrantyDuration) || undefined : undefined,
			warrantyUnit: value.warrantyUnit,
			warrantyTerms: value.warrantyEnabled ? value.warrantyTerms?.trim() || undefined : undefined,
			warrantyProvider: value.warrantyEnabled ? value.warrantyProvider?.trim() || undefined : undefined,
			requiresSerialNumber: Boolean(value.warrantyEnabled && value.requiresSerialNumber),
			categoryId: value.categoryId,
			branchId: value.branchId,
		};
	};

	const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = e => {
				setProduct(prev => ({
					...prev,
					image: e.target?.result as string,
				}));
			};
			reader.readAsDataURL(file);
		}
	};

	const handleAddCategory = async () => {
		if (!newCategoryName.trim()) {
			toast({
				title: t("common.error"),
				description: t("categories.errors.nameRequired"),
				variant: "destructive",
			});
			return;
		}

		try {
			const newCategory = await categoriesAPI.create({
				name: newCategoryName,
				status: "active",
			});
			setCategories([...categories, newCategory]);
			setProduct(prev => ({ ...prev, categoryId: newCategory.id }));
			setNewCategoryName("");
			setIsAddCategoryDialogOpen(false);
			toast({
				title: t("common.success"),
				description: t("categories.messages.created"),
			});
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		}
	};

	const handleSubmit = async () => {
		const barcodeErr = product.unit === "weight" ? "" : barcodeFieldError(product.barcode ?? "");
		if (barcodeErr) {
			setBarcodeError(barcodeErr);
			toast({ title: t("common.error"), description: t(barcodeErr), variant: "destructive" });
			return;
		}
		if (composed && (configurationError || configurationLoading)) return;
		if (composed && (!isEditing || configurationDirty)) {
			const lines = configuration.variants.length
				? configuration.variants.flatMap(v => v.components)
				: product.type === "menu_item"
					? configuration.ingredients
					: configuration.components;
			if (
				(!lines.length && (product.type !== "menu_item" || canWriteRecipe)) ||
				lines.some(line => !Number.isFinite(line.quantity) || line.quantity <= 0) ||
				configuration.variants.some(
					v =>
						!v.name.trim() ||
						((product.type !== "menu_item" || canWriteRecipe) && !v.components.length) ||
						!Number.isFinite(v.sellingPrice) ||
						v.sellingPrice < 0,
				) ||
				configuration.groups.some(
					g =>
						!g.name.trim() ||
						!g.options.length ||
						g.minSelections > g.maxSelections ||
						g.maxSelections > g.options.length ||
						g.options.some(
							o =>
								!o.name.trim() ||
								!Number.isFinite(o.priceAdjustment) ||
								displaySellingPrice + o.priceAdjustment < 0,
						),
				)
			) {
				toast({
					title: t("common.error"),
					description: t("products.configuration.invalid"),
					variant: "destructive",
				});
				return;
			}
		}
		try {
			setSaving(true);
			const payload = {
				...buildProductPayload(product),
				...(composed && (!isEditing || configurationDirty)
					? {
							configuration: {
								...(product.type === "menu_item" && canWriteRecipe && !configuration.variants.length
									? {
											ingredients: configuration.ingredients.map(({ product, ...line }) => ({
												productId: line.productId,
												quantity: line.quantity,
												unit: line.unit,
												wastePercent: line.wastePercent,
												isOptional: line.isOptional,
											})),
										}
									: {}),
								...(product.type === "combo" && !configuration.variants.length
									? {
											components: configuration.components.map(line => ({
												variantId: line.variantId,
												productId: line.productId,
												quantity: line.quantity,
											})),
										}
									: {}),
								...(product.type !== "menu_item" || canWriteRecipe
									? {
											variants: configuration.variants.map(v => ({
												id: v.id,
												name: v.name,
												sellingPrice: v.sellingPrice,
												components: v.components.map(line => ({
													productId: line.productId,
													variantId: line.variantId,
													quantity: line.quantity,
													unit: line.unit,
													wastePercent: line.wastePercent,
												})),
											})),
										}
									: {}),
								...(canWriteModifiers
									? {
											groups: configuration.groups.map(g => ({
												...g,
												options: g.options.map(({ ingredientProduct, ...o }) => o),
											})),
										}
									: {}),
							},
						}
					: {}),
			};
			if (stockPolicy.dirty && canWriteStockPolicy)
				payload.configuration = {
					...payload.configuration,
					stockPolicy: stockPolicy.draft,
				} as typeof payload.configuration;
			if (isEditing) {
				await productsAPI.update(id!, payload);
				const refreshed = await productsAPI.getById(id!);
				setProduct(refreshed);
				if (composed) await loadConfiguration(refreshed);
				stockPolicy.markSaved();
				markSaved();
				toast({
					title: t("common.success"),
					description: t("products.messages.updated"),
				});
			} else {
				await productsAPI.create(payload);
				toast({
					title: t("common.success"),
					description: t("products.messages.created"),
				});
				allowNavigation();
				navigate("/products");
			}
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const handleTransferSubmit = async () => {
		const fromId = product.branchId;
		if (!fromId || !product.id) {
			toast({
				title: t("common.error"),
				description: t("stockTransfers.errors.selectBranches"),
				variant: "destructive",
			});
			return;
		}
		if (!transferToBranchId || transferToBranchId === fromId) {
			toast({
				title: t("common.error"),
				description: t("stockTransfers.errors.sameBranch"),
				variant: "destructive",
			});
			return;
		}
		if (transferQty <= 0) {
			toast({
				title: t("common.error"),
				description: t("stockTransfers.errors.addItems"),
				variant: "destructive",
			});
			return;
		}
		if (product.trackQuantity && transferQty > product.quantity) {
			toast({
				title: t("common.error"),
				description: t("products.transfer.exceedsOnHand"),
				variant: "destructive",
			});
			return;
		}
		try {
			setTransferSubmitting(true);
			const created = await stockTransfersAPI.create({
				fromBranchId: fromId,
				toBranchId: Number(transferToBranchId),
				items: [{ productId: product.id, quantity: transferQty }],
				notes: transferNotes.trim() || undefined,
			});
			toast({ title: t("common.success"), description: t("stockTransfers.messages.created") });
			setTransferOpen(false);
			setTransferNotes("");
			const tid = created?.id ?? created?.data?.id;
			if (tid) navigate(`/stock-transfers/${tid}`);
			else navigate("/products");
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("stockTransfers.errors.createFailed"),
				variant: "destructive",
			});
		} finally {
			setTransferSubmitting(false);
		}
	};

	if (loading) return <Loading />;

	return (
		<div className="product-editor container mx-auto max-w-[1680px] space-y-3 px-3 py-3 md:px-5">
			<div className="grid grid-cols-1 gap-4">
				<header className="page-header">
					<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
						<div className="space-y-2">
							<PageHeading className="flex items-center gap-2">
								<Package className="w-6 h-6" />
								<span>
									{isViewOnly
										? t("common.view")
										: isEditing
											? t("products.actions.edit")
											: t("products.actions.create")}
								</span>
							</PageHeading>
							<CardDescription>
								{isViewOnly
									? t("products.form.viewDescription")
									: isEditing
										? t("products.form.editDescription")
										: t("products.form.createDescription")}
							</CardDescription>
						</div>
						<div className="flex flex-wrap items-center gap-2 sm:justify-end">
							<PermissionGate permission="products.readCrossBranch">
								{isEditing && (
									<Button variant="outline" size="sm" asChild className="h-12">
										<Link
											to={`/products?crossBranch=1&q=${encodeURIComponent(product.barcode || product.name || String(product.id))}`}
										>
											{t("products.seeOtherBranches")}
										</Link>
									</Button>
								)}
							</PermissionGate>
							<PermissionGate permission="stockTransfers.create">
								{isEditing && product.branchId && product.trackQuantity && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="h-12"
										onClick={() => {
											setTransferOpen(true);
											setTransferToBranchId("");
											setTransferQty(1);
											setTransferNotes("");
										}}
									>
										<Truck className="me-1.5 h-4 w-4" />
										{t("products.transfer.title")}
									</Button>
								)}
							</PermissionGate>
						</div>
					</div>
				</header>

				<div className="product-editor-layout">
					<ProductPreview
						fromPrice={composed && configuration.variants.length > 0}
						product={{ ...product, sellingPrice: displaySellingPrice }}
						categoryName={
							categories.find(category => category.id === product.categoryId)?.name ??
							(product.category?.id === product.categoryId ? product.category?.name : undefined)
						}
					/>
					<div className="product-editor-main">
						<div className="product-editor-surface product-core-form">
							<section className="product-identity-panel">
								<div className="product-identity">
									<div className="product-photo-editor">
										<div className="product-photo-preview">
											{product.image ? (
												<img
													src={getDashboardProductImageSrc(product.image)}
													alt={product.name}
												/>
											) : (
												<Package className="h-10 w-10 text-muted-foreground" aria-hidden />
											)}
										</div>
										{!isViewOnly && (
											<>
												<Label
													htmlFor="product-image"
													className="product-photo-action"
													title={t("products.form.image")}
												>
													<Camera className="h-4 w-4" aria-hidden="true" />
													<span className="sr-only">{t("products.form.image")}</span>
												</Label>
												<Input
													id="product-image"
													aria-label={t("products.form.image")}
													type="file"
													accept="image/*"
													onChange={handleImageUpload}
													className="product-photo-input"
												/>
											</>
										)}
									</div>
									<div className="product-identity-fields">
										<div className="space-y-1.5">
											<Label htmlFor="name">
												{t("products.form.name")}
												{!isViewOnly && <span className="text-destructive"> *</span>}
											</Label>
											<Input
												id="name"
												required
												value={product.name}
												onChange={e => handleInputChange("name", e.target.value)}
												placeholder={t("products.form.namePlaceholder")}
												readOnly={isViewOnly}
												className={isViewOnly ? "bg-muted" : ""}
											/>
										</div>
										<div className="space-y-1.5">
											<Label htmlFor="categoryId">{t("products.form.category")}</Label>
											<div className="flex gap-2">
												<Select
													value={product.categoryId != null ? String(product.categoryId) : ""}
													onValueChange={value =>
														handleInputChange(
															"categoryId",
															value ? Number(value) : undefined,
														)
													}
													disabled={isViewOnly}
												>
													<SelectTrigger
														aria-label={t("products.form.category")}
														className={isViewOnly ? "bg-muted" : ""}
													>
														<SelectValue placeholder={t("products.form.selectCategory")} />
													</SelectTrigger>
													<SelectContent>
														{categories.map(category => (
															<SelectItem key={category.id} value={String(category.id)}>
																{category.name}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												{!isViewOnly && (
													<Button
														type="button"
														variant="outline"
														size="sm"
														className="h-12 w-12"
														onClick={() => setIsAddCategoryDialogOpen(true)}
														title={t("categories.actions.add")}
														aria-label={t("categories.actions.add")}
													>
														<Plus className="h-4 w-4" />
													</Button>
												)}
											</div>
										</div>
									</div>
								</div>
								<div
									className={`product-field-grid${product.unit === "weight" ? " product-field-grid-weight" : ""}`}
								>
									<div className="space-y-1.5">
										<Label htmlFor="type">{t("products.form.type")}</Label>
										<Select
											value={product.type}
											onValueChange={(value: "product" | "service" | "menu_item" | "combo") =>
												handleInputChange("type", value)
											}
											disabled={isViewOnly}
										>
											<SelectTrigger
												aria-label={t("products.form.type")}
												className={isViewOnly ? "bg-muted" : ""}
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="product">{t("products.types.product")}</SelectItem>
												<SelectItem value="service">{t("products.types.service")}</SelectItem>
												<SelectItem value="menu_item">
													{t("products.types.menuItem")}
												</SelectItem>
												<SelectItem value="combo">{t("products.types.combo")}</SelectItem>
											</SelectContent>
										</Select>
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="unit">{t("products.form.unit")}</Label>
										<Select
											value={product.unit}
											onValueChange={(value: "piece" | "bulk" | "weight") =>
												handleInputChange("unit", value)
											}
											disabled={isViewOnly}
										>
											<SelectTrigger
												aria-label={t("products.form.unit")}
												className={isViewOnly ? "bg-muted" : ""}
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="piece">{t("products.units.piece")}</SelectItem>
												<SelectItem value="bulk">{t("products.units.bulk")}</SelectItem>
												<SelectItem value="weight">{t("products.units.weight")}</SelectItem>
											</SelectContent>
										</Select>
									</div>
									{product.unit !== "weight" && (
										<div className="space-y-1.5">
											<Label htmlFor="barcode">{t("products.form.barcode")}</Label>
											<div className="flex items-stretch gap-1">
												<Input
													id="barcode"
													value={product.barcode}
													onChange={e => onBarcodeChange(e.target.value)}
													readOnly={isViewOnly}
													placeholder={t("products.form.barcodePlaceholder")}
													aria-invalid={barcodeError ? true : undefined}
													className={
														isViewOnly
															? "bg-muted rounded-e-none"
															: `min-h-12 rounded-e-none${barcodeError ? " border-destructive" : ""}`
													}
												/>
												{!isViewOnly && (
													<Button
														type="button"
														variant="outline"
														size="sm"
														className="bg-muted h-12 w-12 rounded-s-none"
														onClick={generateBarcode}
														disabled={generatingBarcode}
														title={t("products.form.generateBarcode")}
														aria-label={t("products.form.generateBarcode")}
													>
														<Wand2 className="h-4 w-4" />
													</Button>
												)}
											</div>
											{barcodeError ? (
												<p className="text-destructive text-xs">{t(barcodeError)}</p>
											) : null}
										</div>
									)}
								</div>
								{product.unit === "weight" && (
									<ProductScalePanel
										embedded
										plu={product.plu ?? ""}
										onChange={value => handleInputChange("plu", value)}
										readOnly={isViewOnly}
									/>
								)}
							</section>
							<section className="product-price-panel" aria-label={t("products.tabs.pricing")}>
								<div className="space-y-4">
									<div className="product-price-fields">
										<div className="space-y-1.5">
											<Label htmlFor="sellingPrice">
												{t(
													composed && configuration.variants.length
														? "productSetup.fromPrice"
														: "products.form.sellingPrice",
												)}
											</Label>
											<Input
												id="sellingPrice"
												type="number"
												min="0"
												step="0.01"
												value={
													configuration.variants.length && composed
														? Math.min(...configuration.variants.map(v => v.sellingPrice))
														: product.sellingPrice
												}
												onChange={e =>
													handleInputChange("sellingPrice", parseFloat(e.target.value) || 0)
												}
												readOnly={isViewOnly || (composed && configuration.variants.length > 0)}
												className={isViewOnly ? "bg-muted" : ""}
											/>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="purchasePrice">
												{t(composed ? "productSetup.cost" : "products.form.purchasePrice")}
											</Label>
											<Input
												id="purchasePrice"
												type="number"
												min="0"
												step="0.01"
												value={
													composed ? Number(calculatedCost.toFixed(2)) : product.purchasePrice
												}
												onChange={e =>
													handleInputChange("purchasePrice", parseFloat(e.target.value) || 0)
												}
												readOnly={isViewOnly || composed}
												className={isViewOnly ? "bg-muted" : ""}
											/>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="discountRate">
												{t("products.form.discountPercentage")}
											</Label>
											<Input
												id="discountRate"
												type="number"
												min="0"
												max="100"
												step="0.01"
												value={product.discountRate}
												onChange={e =>
													handleInputChange("discountRate", parseFloat(e.target.value) || 0)
												}
												readOnly={isViewOnly}
												className={isViewOnly ? "bg-muted" : ""}
											/>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="discountValue">{t("products.form.discountAmount")}</Label>
											<Input
												id="discountValue"
												type="number"
												min="0"
												step="0.01"
												value={product.discountValue}
												onChange={e =>
													handleInputChange("discountValue", parseFloat(e.target.value) || 0)
												}
												readOnly={isViewOnly}
												className={isViewOnly ? "bg-muted" : ""}
											/>
										</div>
									</div>

									<section className="product-tax-row" aria-label={t("products.form.tax")}>
										<div className="space-y-1.5">
											<div className="flex flex-wrap items-center justify-between gap-2">
												<Label htmlFor="taxId">{t("products.form.tax")}</Label>
												{canReadTaxes && (
													<Link
														className="text-xs text-primary underline underline-offset-4"
														to="/payments?type=taxes"
													>
														{t("paymentTaxDesign.manageTaxes")}
													</Link>
												)}
											</div>
											<Select
												value={taxSelectValue}
												onValueChange={handleTaxSelect}
												disabled={isViewOnly || taxesLoading || taxesError}
											>
												<SelectTrigger id="taxId" className={isViewOnly ? "bg-muted" : ""}>
													<SelectValue placeholder={t("products.form.selectTax")} />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value={TAX_EXEMPT}>
														{t("products.form.taxExempt")}
													</SelectItem>
													{taxSelectValue === TAX_LEGACY && (
														<SelectItem value={TAX_LEGACY}>
															{t("products.form.taxCurrentRate", {
																rate: product.taxRate,
															})}
														</SelectItem>
													)}
													{product.taxId != null &&
														!taxes.some(
															tax => String(tax.id) === String(product.taxId),
														) && (
															<SelectItem value={String(product.taxId)}>
																{t("products.form.taxCurrentRate", {
																	rate: product.taxRate,
																})}
															</SelectItem>
														)}
													{taxes.map(tax => (
														<SelectItem key={tax.id} value={String(tax.id)}>
															<bdi>{tax.name}</bdi> <bdi>({Number(tax.rate)}%)</bdi>
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
										{taxesLoading ? (
											<p role="status" className="text-sm text-muted-foreground">
												{t("common.loading")}
											</p>
										) : taxesError ? (
											<div role="alert" className="flex flex-wrap items-center gap-2 text-sm">
												<p>{t("paymentTaxDesign.taxOptionsFailed")}</p>
												<Button type="button" variant="outline" size="sm" onClick={fetchTaxes}>
													{t("common.retry")}
												</Button>
											</div>
										) : (
											taxes.length === 0 && (
												<p className="text-sm text-muted-foreground">
													{t("paymentTaxDesign.noTaxOptions")}
												</p>
											)
										)}
										<TaxExample
											rate={Number(product.taxRate)}
											amount={displaySellingPrice}
											product
											compact
										/>
									</section>
									{/* Live pricing summary */}
									{(() => {
										const selling = displaySellingPrice || 0;
										const purchase = calculatedCost || 0;
										const profit = selling - purchase;
										const margin = selling > 0 ? Math.round((profit / selling) * 100) : 0;
										return (
											<div className="product-profit-summary">
												<div className="text-start">
													<div
														className={`text-sm font-bold ${profit > 0 ? "text-success" : profit < 0 ? "text-destructive" : ""}`}
													>
														<Price value={profit} currency={currency} />
													</div>
													<div className="mt-1 text-xs text-muted-foreground">
														{t("products.pricingSummary.profit")}
													</div>
												</div>
												<div className="text-start">
													<div
														className={`text-sm font-bold ${profit > 0 ? "text-success" : profit < 0 ? "text-destructive" : ""}`}
													>
														{margin}%
													</div>
													<div className="mt-1 text-xs text-muted-foreground">
														{t("products.pricingSummary.margin")}
													</div>
												</div>
											</div>
										);
									})()}
								</div>
							</section>

							<section
								className="product-stock-panel"
								data-tracking={product.type === "product" && product.trackQuantity}
								aria-label={t("productEditor.stockLocation")}
							>
								<div className="space-y-1.5">
									<Label htmlFor="branchId">{t("products.form.branch")}</Label>
									<Select
										value={product.branchId != null ? String(product.branchId) : ""}
										onValueChange={value =>
											handleInputChange("branchId", value ? Number(value) : undefined)
										}
										disabled={isViewOnly}
									>
										<SelectTrigger
											aria-label={t("products.form.branch")}
											className={isViewOnly ? "bg-muted" : ""}
										>
											<SelectValue placeholder={t("products.form.selectBranch")} />
										</SelectTrigger>
										<SelectContent>
											{branches.map(branch => (
												<SelectItem key={branch.id} value={String(branch.id)}>
													{branch.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								{product.type === "product" ? (
									<div className="space-y-1.5">
										<Label htmlFor="trackQuantity">{t("products.form.inventory")}</Label>
										<Select
											value={product.trackQuantity ? "track" : "no-track"}
											onValueChange={value =>
												handleInputChange("trackQuantity", value === "track")
											}
											disabled={isViewOnly}
										>
											<SelectTrigger
												aria-label={t("products.form.inventory")}
												className={isViewOnly ? "bg-muted" : ""}
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="track">{t("products.inventory.track")}</SelectItem>
												<SelectItem value="no-track">
													{t("products.inventory.noTrack")}
												</SelectItem>
											</SelectContent>
										</Select>
									</div>
								) : (
									<p className="text-sm text-muted-foreground self-center">
										{t(
											product.type === "service"
												? "productSetup.serviceStock"
												: "productSetup.componentStock",
										)}
									</p>
								)}
								{product.type === "product" && product.trackQuantity && (
									<div className="product-stock-fields">
										<div className="space-y-1.5">
											<Label htmlFor="quantity">{t("products.form.quantity")}</Label>
											<Input
												id="quantity"
												type="number"
												step={product.unit === "weight" ? "0.001" : "1"}
												min="0"
												value={product.quantity}
												onChange={e =>
													handleInputChange(
														"quantity",
														(product.unit === "weight"
															? parseFloat(e.target.value)
															: parseInt(e.target.value, 10)) || 0,
													)
												}
												readOnly={isViewOnly}
												className={isViewOnly ? "bg-muted" : ""}
											/>
										</div>
										{product.type === "product" && product.trackQuantity && (
											<div className="space-y-1.5">
												<Label htmlFor="lowStockAlert">
													{t("products.form.lowStockThreshold")}
												</Label>
												<Input
													id="lowStockAlert"
													type="number"
													step="1"
													min="0"
													value={product.lowStockAlert}
													onChange={e =>
														handleInputChange(
															"lowStockAlert",
															parseInt(e.target.value, 10) || 0,
														)
													}
													readOnly={isViewOnly}
													className={isViewOnly ? "bg-muted" : ""}
												/>
											</div>
										)}
									</div>
								)}
							</section>
						</div>

						{composed &&
							(configurationLoading ? (
								<p role="status">{t("common.loading")}</p>
							) : configurationError ? (
								<div role="alert" className="rounded-xl border p-4">
									<p>{t("productSetup.loadError")}</p>
									<Button variant="outline" onClick={() => loadConfiguration()}>
										{t("common.retry")}
									</Button>
								</div>
							) : (
								<ProductSetupEditor
									type={product.type}
									productId={product.id}
									branchId={product.branchId}
									value={configuration}
									onChange={setConfiguration}
									basePrice={Number(product.sellingPrice)}
									disabled={isViewOnly}
									recipeDisabled={!canReadRecipe || !canWriteRecipe}
									groupsDisabled={!canReadModifiers || !canWriteModifiers}
								/>
							))}
						{product.type === "menu_item" && canReadStockPolicy && (
							<div className="rounded-2xl bg-card p-4">
								<RecipeStockSettingsFields
									model={stockPolicy}
									disabled={isViewOnly || saving || !canWriteStockPolicy}
								/>
							</div>
						)}
						<div className="product-additional-details">
							<section className="product-editor-surface">
								<div className="space-y-1.5">
									<Label htmlFor="description">{t("products.form.description")}</Label>
									<Textarea
										id="description"
										value={product.description}
										onChange={e => handleInputChange("description", e.target.value)}
										placeholder={t("products.form.descriptionPlaceholder")}
										rows={3}
										readOnly={isViewOnly}
										className={isViewOnly ? "bg-muted" : ""}
									/>
								</div>
							</section>{" "}
							<section
								className="product-editor-surface product-warranty-panel"
								aria-labelledby="product-warranty-heading"
							>
								<h2 id="product-warranty-heading" className="font-semibold text-sm">
									{t("products.warranty.title")}
								</h2>

								<div className="space-y-4 pt-3">
									<div className="flex items-center justify-between rounded-lg border p-3">
										<Label htmlFor="warrantyEnabled" className="text-sm font-medium">
											{t("products.warranty.enabled")}
										</Label>
										<Switch
											id="warrantyEnabled"
											checked={product.warrantyEnabled}
											onCheckedChange={checked => handleInputChange("warrantyEnabled", checked)}
											disabled={isViewOnly}
										/>
									</div>

									{product.warrantyEnabled && (
										<div className="space-y-4">
											<div className="grid grid-cols-2 gap-4">
												<div className="space-y-1.5">
													<Label htmlFor="warrantyDuration">
														{t("products.warranty.duration")}
													</Label>
													<Input
														id="warrantyDuration"
														type="number"
														min={0}
														value={product.warrantyDuration}
														onChange={e =>
															handleInputChange("warrantyDuration", e.target.value)
														}
														readOnly={isViewOnly}
													/>
												</div>
												<div className="space-y-1.5">
													<Label>{t("products.warranty.unit")}</Label>
													<Select
														value={product.warrantyUnit ?? "months"}
														onValueChange={v => handleInputChange("warrantyUnit", v)}
														disabled={isViewOnly}
													>
														<SelectTrigger aria-label={t("products.warranty.unit")}>
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="days">
																{t("products.warranty.unitDays")}
															</SelectItem>
															<SelectItem value="months">
																{t("products.warranty.unitMonths")}
															</SelectItem>
															<SelectItem value="years">
																{t("products.warranty.unitYears")}
															</SelectItem>
														</SelectContent>
													</Select>
												</div>
											</div>
											<div className="space-y-1.5">
												<Label htmlFor="warrantyProvider">
													{t("products.warranty.provider")}
												</Label>
												<Input
													id="warrantyProvider"
													value={product.warrantyProvider}
													onChange={e =>
														handleInputChange("warrantyProvider", e.target.value)
													}
													readOnly={isViewOnly}
												/>
											</div>
											<div className="space-y-1.5">
												<Label htmlFor="warrantyTerms">{t("products.warranty.terms")}</Label>
												<Input
													id="warrantyTerms"
													value={product.warrantyTerms}
													onChange={e => handleInputChange("warrantyTerms", e.target.value)}
													readOnly={isViewOnly}
												/>
											</div>
											<div className="flex items-center justify-between rounded-lg border p-3">
												<Label htmlFor="requiresSerialNumber" className="text-sm font-medium">
													{t("products.warranty.requiresSerial")}
												</Label>
												<Switch
													id="requiresSerialNumber"
													checked={product.requiresSerialNumber}
													onCheckedChange={checked =>
														handleInputChange("requiresSerialNumber", checked)
													}
													disabled={isViewOnly}
												/>
											</div>
										</div>
									)}
								</div>
							</section>
						</div>

						<ProductLabel
							variants={composed ? configuration.variants : []}
							name={product.name}
							barcode={product.barcode}
							price={Number(product.sellingPrice)}
							taxRate={Number(product.taxRate)}
							weightBased={product.unit === "weight"}
							saved={isEditing && !isDirty && !(composed && configurationDirty)}
						/>
					</div>
				</div>
				{/* Sticky action bar */}
				{canSave && !isViewOnly && (
					<div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 rounded-t-lg border-t bg-background/95 px-3 py-3 backdrop-blur">
						<Button type="button" variant="outline" onClick={() => navigate("/products")} disabled={saving}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleSubmit} disabled={saving} className="w-full sm:w-32">
							{saving ? (
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-2"></div>
							) : (
								<Save className="h-4 w-4 mx-2" />
							)}
							{t("common.save")}
						</Button>
					</div>
				)}
			</div>

			{/* Add Category Dialog */}
			<Dialog open={isAddCategoryDialogOpen} onOpenChange={setIsAddCategoryDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("categories.dialogs.create.title")}</DialogTitle>
					</DialogHeader>
					<div className="">
						<div className="space-y-2">
							<Label htmlFor="newCategoryName">{t("categories.fields.name")}</Label>
							<Input
								id="newCategoryName"
								value={newCategoryName}
								onChange={e => setNewCategoryName(e.target.value)}
								placeholder={t("categories.fields.namePlaceholder")}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setIsAddCategoryDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleAddCategory}>{t("common.create")}</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<PermissionGate permission="stockTransfers.create">
				<Dialog open={transferOpen} onOpenChange={setTransferOpen}>
					<DialogContent className="max-w-lg">
						<DialogHeader>
							<DialogTitle>{t("products.transfer.title")}</DialogTitle>
							<DialogDescription>{t("products.transfer.description")}</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 py-2">
							<div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
								<div>
									<span className="text-muted-foreground">{t("products.form.name")}: </span>
									<span className="font-medium">{product.name}</span>
								</div>
								<div>
									<span className="text-muted-foreground">
										{t(product.unit === "weight" ? "products.form.plu" : "products.form.barcode")}
										:{" "}
									</span>
									<span>{(product.unit === "weight" ? product.plu : product.barcode) || "-"}</span>
								</div>
								<div>
									<span className="text-muted-foreground">{t("products.form.branch")}: </span>
									<span>
										{product.branch?.name ??
											branches.find(b => b.id === product.branchId)?.name ??
											"-"}
									</span>
								</div>
								<div>
									<span className="text-muted-foreground">{t("products.transfer.onHand")}: </span>
									<span>{product.quantity}</span>
								</div>
							</div>
							<div className="space-y-2">
								<Label>{t("stockTransfers.toBranch")}</Label>
								<Select
									value={transferToBranchId === "" ? "" : String(transferToBranchId)}
									onValueChange={v => setTransferToBranchId(v ? parseInt(v, 10) : "")}
								>
									<SelectTrigger aria-label={t("stockTransfers.toBranch")}>
										<SelectValue placeholder={t("stockTransfers.selectBranch")} />
									</SelectTrigger>
									<SelectContent>
										{branches
											.filter(b => b.id !== product.branchId)
											.map(b => (
												<SelectItem key={b.id} value={String(b.id)}>
													{b.name}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label htmlFor="transferQty">{t("stockTransfers.quantity")}</Label>
								<Input
									id="transferQty"
									type="number"
									min={1}
									step={1}
									value={transferQty}
									onChange={e => setTransferQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="transferNotes">{t("common.notes")}</Label>
								<Input
									id="transferNotes"
									value={transferNotes}
									onChange={e => setTransferNotes(e.target.value)}
									placeholder={t("common.optional")}
								/>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setTransferOpen(false)}
								disabled={transferSubmitting}
							>
								{t("common.cancel")}
							</Button>
							<Button onClick={handleTransferSubmit} disabled={transferSubmitting}>
								{transferSubmitting ? (
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
								) : (
									<span>{t("products.transfer.submit")}</span>
								)}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</PermissionGate>
		</div>
	);
};

export default ProductDetailPage;
